import dayjs from 'dayjs';

import { DATE_FORMAT, API_DATE_FORMAT } from 'utils/dayjs';
import { convertFieldToHumanReadable } from 'utils/formatters/fieldToDisplayableName';

import type {
  PayoutReasonCodeItem,
  PayoutReasonCodeStats,
  PostDisbursementPayoutReportsBodyStatusCategoriesItem
} from 'api';

/**
 * util rendering function
 */
export const renderAmountFromAndTo = (
  amountFrom?: string,
  amountTo?: string
) => {
  if (amountFrom && amountTo) return `${amountFrom} - ${amountTo}`;
  if (amountFrom) return `${amountFrom} - ∞`;
  if (amountTo) return `0 - ${amountTo}`;
  return 'Filter Amount';
};

export function formatDate(stringDate: string) {
  return dayjs(stringDate, DATE_FORMAT).format(API_DATE_FORMAT);
}

export function isReasonCodeNodeValue(
  obj:
    | ReasonCodeNodeValue
    | {
        key: number;
        title: string;
        value: string;
      }
): obj is ReasonCodeNodeValue {
  return typeof obj === 'object' && obj !== null && 'status' in obj;
}

export interface ReasonCodeNodeValue {
  title: string;
  value: string;
  reasonCode?: string;
  status: PostDisbursementPayoutReportsBodyStatusCategoriesItem;
}

/**
 * Function that converts the reason codes to a tree select component
 */
export function mapToTreeData(
  data: PayoutReasonCodeStats | undefined
): ReasonCodeNodeValue[] {
  if (!data) return [];
  return Object.keys(data).map((key) => {
    const node = data[key];
    const status = key as PostDisbursementPayoutReportsBodyStatusCategoriesItem;
    return {
      title: convertFieldToHumanReadable(key),
      value: key,
      reasonCode: undefined,
      status,
      children: node?.items?.map((item: PayoutReasonCodeItem) => ({
        title: convertFieldToHumanReadable(item.name),
        value: `${key}-${item.name}`,
        status,
        reasonCode: item.id.toString()
      }))
    };
  });
}

/**
 * Transforms an array of key-value pairs into an object.
 * Is tested in payouts utils.ts
 *
 * @example
 * Input:
 * [
 *   { "key": "creditor_account.account_number", "value": "00123456789" },
 *   { "key": "creditor_account.bank_bic", "value": "BLCMCAMMXXX" },
 *   { "key": "creditor_account.bank_clearing_system_code", "value": "CACPA" },
 *   { "key": "creditor_account.bank_clearing_system_id", "value": "030300012" },
 *   { "key": "creditor_account.bank_name", "value": "LAURENTIAN BANK OF CANADA" },
 *   { "key": "creditor_account.type", "value": "CACC" }
 * ]
 *
 * Output (format expected by our form):
 * {
 *   creditor_account[account_number]: "00123456789",
 *   creditor_account[bank_bic]: "BLCMCAMMXXX",
 *   creditor_account[bank_clearing_system_code]: "CACPA",
 *   creditor_account[bank_clearing_system_id]: "030300012",
 *   creditor_account[bank_name]: "LAURENTIAN BANK OF CANADA",
 *   creditor_account[type]: "CACC"
 * }
 */
export function transformToObject<T extends { key: string; value: string }>(
  array: T[]
): Record<string, Record<string, string>> {
  return array.reduce(
    (acc, { key, value }) => {
      const [section, subKey] = key.split('.', 2);
      if (!acc[section]) {
        acc[section] = {};
      }
      acc[section][subKey] = value;
      return acc;
    },
    {} as Record<string, Record<string, string>>
  );
}
